import Button from '../button'
import { ExclamationOutlined } from '@ant-design/icons'
import { Col, Row } from 'antd'
import TextArea from 'antd/lib/input/TextArea'
import { useModal } from 'hooks'
import { ReactNode, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

const StyledConfirmModal = styled.div`
  padding: 32px;
`
const StyledContent = styled.div`
  position: relative;
  display: flex;
  justify-content: center;
  flex-direction: column;
  align-items: center;
  margin-bottom: 24px;
  label {
    margin-bottom: 12px;
  }
  textarea {
    resize: none;
    height: 120px;
  }
`
const StyledLabel = styled.div`
  margin-bottom: 12px;
  font-weight: 700;
`

const StyledMessage = styled.div`
  position: absolute;
  bottom: 0px;
  color: var(--color-red-106);
`

const StyledTitle = styled.div`
  font-weight: 700;
  font-size: 1.6rem;
  text-align: justify;
  margin-bottom: 16px;
`

const StyledTitleCustom = styled.div`
  font-weight: 700;
  font-size: 1.6rem;
  text-align: justify;
  margin-bottom: 16px;
  text-transform: uppercase;
  color: var(--color-gray-105);
`

const StyledDescription = styled.div`
  font-weight: 400;
  font-size: 1.4rem;
  color: var(--color-gray-105);
  margin-bottom: 16px;
`

const StyledDescriptionCustom = styled.div`
  font-weight: 700;
  text-transform: uppercase;
  color: var(--color-red-106);
`
const StyledImg = styled.img`
  width: 100px;
  height: 100px;
  margin-bottom: 24px;
`
const StyledReason = styled.div`
  width: 100%;
`

interface IProps {
  title?: string
  titleCustom?: string
  children?: ReactNode
  icon?: string
  description?: string | ReactNode
  descriptionCustom?: string
  cancelLabel?: string
  okLabel?: string
  allowClickOutsideBtn?: boolean
  colorBtnOk?: 'red' | 'green'
  showReason?: boolean
}

export enum ActionType {
  Edit,
  Delete,
  Reload,
  Accept
}

const ConfirmModal = (props: IProps) => {
  const { close, isOpen } = useModal()
  const {
    icon,
    description,
    descriptionCustom,
    title,
    titleCustom,
    allowClickOutsideBtn,
    cancelLabel = 'cancel',
    okLabel = 'confirm',
    colorBtnOk = 'green',
    showReason,
    children
  } = props
  const [reason, setReason] = useState('')
  const [invalid, setInvalid] = useState(false)
  const { t } = useTranslation()

  useEffect(() => {
    if (!isOpen) setReason('')
  }, [isOpen])

  const handleOnSubmit = () => {
    if (!reason.trim() && showReason) {
      setInvalid(true)
      return
    }
    close(reason || true)
  }

  const handleOnChange = (e: any) => {
    setReason(e.target.value)
    setInvalid(false)
  }
  return (
    <StyledConfirmModal>
      <StyledContent>
        {!!icon && <StyledImg src={icon} alt="" />}
        {!!title && <StyledTitle>{t(title)}</StyledTitle>}
        {!!titleCustom && (
          <StyledTitleCustom>{t(titleCustom)}</StyledTitleCustom>
        )}
        {!!descriptionCustom && (
          <>
            <StyledDescriptionCustom>
              <ExclamationOutlined style={{ fontSize: '28px' }} />
              <span> {t(descriptionCustom)}</span>
            </StyledDescriptionCustom>
          </>
        )}
        {!!description && (
          <StyledDescription>
            {typeof description === 'string' ? t(description) : description}
          </StyledDescription>
        )}

        {!!children && children}

        {showReason && (
          <StyledReason>
            <StyledLabel>{t('reason')}</StyledLabel>
            <TextArea
              allowClear
              status={invalid ? 'error' : ''}
              showCount
              maxLength={255}
              onChange={handleOnChange}
              value={reason}
              style={{ height: 120, marginBottom: 24 }}
            />
            {invalid && (
              <StyledMessage>{t('message-reason-blank')}</StyledMessage>
            )}
          </StyledReason>
        )}
      </StyledContent>
      <Row gutter={16}>
        <Col span={12}>
          <Button
            allowClickOutside={allowClickOutsideBtn}
            noBackground
            fullWidth
            onClick={() => close(false)}>
            {t(cancelLabel)}
          </Button>
        </Col>
        <Col span={12}>
          <Button fullWidth color={colorBtnOk} onClick={handleOnSubmit}>
            {t(okLabel)}
          </Button>
        </Col>
      </Row>
    </StyledConfirmModal>
  )
}

export default ConfirmModal
